import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { formatMoney, formatDate } from "@/lib/format";
import { CreditCard, Download, CheckCircle2, ShieldCheck, Loader2 } from "lucide-react";

const STATUS_COLOR = {
  draft: "bg-white/[0.08] text-white",
  sent: "bg-blue-500/15 text-blue-300",
  paid: "bg-[color:var(--bd-green)]/20 text-[color:var(--bd-green-bright)]",
  overdue: "bg-[color:var(--bd-red)]/20 text-[color:var(--bd-red-bright)]",
  cancelled: "bg-white/[0.04] text-[color:var(--ivory-muted)]",
};

export default function InvoicePay() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isSuccess = location.pathname.endsWith("/success");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/invoice/${token}`);
      setInv(data);
    } catch {
      toast.error("Invoice not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  // Polling for success
  useEffect(() => {
    if (!isSuccess) return;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;

    let attempts = 0;
    const max = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Payment received — thank you!");
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Payment session expired");
          navigate(`/pay/${token}`);
          return;
        }
        if (attempts < max) setTimeout(poll, 2500);
      } catch {
        if (attempts < max) setTimeout(poll, 2500);
      }
    };
    poll();
  }, [isSuccess, location.search, token, navigate]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        invoice_token: token,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#07090B] text-white"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (!inv) {
    return <div className="min-h-screen flex items-center justify-center bg-[#07090B] text-white">Invoice not found.</div>;
  }

  const balance = inv.total - (inv.amount_paid || 0);
  const paid = balance <= 0.01 || inv.status === "paid";

  return (
    <div className="min-h-screen bg-[#07090B] text-[color:var(--ivory)] py-12 px-4" data-testid="invoice-pay-page">
      <div className="bg-stage" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="bg-grid" />
        <div className="orb orb-green" />
      </div>
      <div className="relative z-10 max-w-3xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[color:var(--ivory-muted)]">Bangladesh Travels &amp; Tours AB</div>
            <h1 className="font-display text-3xl sm:text-4xl mt-1">Invoice {inv.invoice_number}</h1>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs uppercase tracking-wide ${STATUS_COLOR[inv.status] || STATUS_COLOR.draft}`}>{inv.status}</span>
        </div>

        {paid && (
          <div className="mb-6 p-5 rounded-2xl border border-[color:var(--bd-green)]/30 bg-[color:var(--bd-green)]/10 flex items-center gap-3" data-testid="paid-banner">
            <CheckCircle2 className="w-6 h-6 text-[color:var(--bd-green-bright)]" />
            <div>
              <div className="font-display text-lg">Thank you — this invoice is fully paid.</div>
              <div className="text-sm text-[color:var(--ivory-muted)]">A receipt has been recorded.</div>
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 space-y-6">
          <div className="grid sm:grid-cols-2 gap-6 text-sm">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2">Bill to</div>
              <div className="font-medium">{inv.customer_snapshot?.name}</div>
              <div className="text-[color:var(--ivory-muted)]">{inv.customer_snapshot?.email}</div>
              {inv.customer_snapshot?.address_line1 && <div className="text-[color:var(--ivory-muted)] mt-1">{inv.customer_snapshot.address_line1}, {inv.customer_snapshot.city} {inv.customer_snapshot.country}</div>}
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--ivory-muted)] mb-2">Dates</div>
              <div>Issued: {formatDate(inv.issue_date)}</div>
              <div>Due: {formatDate(inv.due_date)}</div>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-xs uppercase tracking-[0.15em] text-[color:var(--ivory-muted)]">
                <tr><th className="px-3 py-2 text-left">Item</th><th className="px-3 py-2">Qty</th><th className="px-3 py-2">Unit</th><th className="px-3 py-2 text-right">Total</th></tr>
              </thead>
              <tbody>
                {inv.items.map((it, idx) => (
                  <tr key={idx} className="border-t border-white/5">
                    <td className="px-3 py-2">{it.description}</td>
                    <td className="px-3 py-2 text-center">{it.quantity}</td>
                    <td className="px-3 py-2 text-center">{formatMoney(it.unit_price, inv.currency)}</td>
                    <td className="px-3 py-2 text-right">{formatMoney(it.quantity * it.unit_price, inv.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-1.5 text-sm max-w-sm ml-auto">
            <div className="flex justify-between"><span className="text-[color:var(--ivory-muted)]">Subtotal</span><span>{formatMoney(inv.subtotal, inv.currency)}</span></div>
            {inv.discount > 0 && <div className="flex justify-between"><span className="text-[color:var(--ivory-muted)]">Discount</span><span>−{formatMoney(inv.discount, inv.currency)}</span></div>}
            <div className="flex justify-between"><span className="text-[color:var(--ivory-muted)]">VAT</span><span>{formatMoney(inv.vat_amount, inv.currency)}</span></div>
            <div className="flex justify-between font-display text-2xl pt-3 mt-1 border-t border-white/10"><span>Total</span><span>{formatMoney(inv.total, inv.currency)}</span></div>
            {inv.amount_paid > 0 && <div className="flex justify-between text-[color:var(--bd-green-bright)]"><span>Paid</span><span>{formatMoney(inv.amount_paid, inv.currency)}</span></div>}
            {!paid && <div className="flex justify-between font-semibold text-[color:var(--bd-red-bright)]"><span>Balance</span><span>{formatMoney(balance, inv.currency)}</span></div>}
          </div>

          {!paid && (
            <div className="pt-4 border-t border-white/5 flex flex-wrap items-center gap-3">
              {inv.currency === "SEK" ? (
                <button
                  onClick={pay}
                  disabled={paying}
                  className="pill pill-primary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="pay-button"
                >
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Pay {formatMoney(balance, inv.currency)} securely
                </button>
              ) : (
                <div className="text-sm text-[color:var(--ivory-muted)] flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Online card payment available for SEK invoices. For BDT, please contact us for bank transfer details.
                </div>
              )}
              <a
                href={`${API}/public/invoice/${token}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="pill border border-white/15 text-white inline-flex items-center gap-2 text-sm"
                data-testid="pdf-button"
              >
                <Download className="w-4 h-4" /> Download PDF
              </a>
            </div>
          )}
        </div>

        <div className="text-center mt-8 text-xs text-[color:var(--ivory-muted)]">
          Questions? Email <a href="mailto:info@bdtt.se" className="text-white hover:underline">info@bdtt.se</a>
        </div>
      </div>
    </div>
  );
}
